import React, { useEffect, useRef, useState } from "react";

/** Format a number as Brazilian currency string (without R$ prefix).
 *  Examples: 1234.5 -> "1.234,50"; 0 -> "0,00"
 */
export function formatBRL(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Parse a BRL-formatted string into a Number. "1.234,56" -> 1234.56 */
export function parseBRL(str) {
  if (str == null) return 0;
  const s = String(str);
  // keep only digits and separators, treat the LAST separator as decimal
  const cleaned = s.replace(/[^\d.,]/g, "");
  if (!cleaned) return 0;
  const lastSep = Math.max(cleaned.lastIndexOf(","), cleaned.lastIndexOf("."));
  if (lastSep === -1) return Number(cleaned.replace(/\D/g, "")) || 0;
  const intPart = cleaned.slice(0, lastSep).replace(/\D/g, "");
  const decPart = cleaned.slice(lastSep + 1).replace(/\D/g, "").slice(0, 2);
  const num = Number(`${intPart || "0"}.${decPart || "0"}`);
  return Number.isFinite(num) ? num : 0;
}

/** Sanitize raw typed input -> { text, numeric } with natural BR typing (comma decimal, max 2). */
function sanitize(raw) {
  const cleaned = String(raw).replace(/[^\d.,]/g, "");
  if (!cleaned) return { text: "", numeric: 0 };
  const lastSep = Math.max(cleaned.lastIndexOf(","), cleaned.lastIndexOf("."));
  let intPart, decPart, hasSep;
  if (lastSep === -1) {
    intPart = cleaned.replace(/\D/g, "");
    decPart = "";
    hasSep = false;
  } else {
    intPart = cleaned.slice(0, lastSep).replace(/\D/g, "");
    decPart = cleaned.slice(lastSep + 1).replace(/\D/g, "").slice(0, 2);
    hasSep = true;
  }
  intPart = intPart.replace(/^0+(?=\d)/, ""); // drop leading zeros
  const text = hasSep ? `${intPart || "0"},${decPart}` : intPart;
  const numeric = Number(`${intPart || "0"}.${decPart || "0"}`) || 0;
  return { text, numeric };
}

/** Number -> editable text (comma decimal, no thousands separators). 0 -> "" */
function toEditText(value) {
  const n = Number(value || 0);
  if (!n) return "";
  return String(n).replace(".", ",");
}

/** Currency input with natural Brazilian typing.
 *  Display formats thousands only on blur; value in/out is a Number (unchanged semantics).
 */
export default function BRLInput({ value, onChange, className = "", placeholder = "0,00", testid, disabled, ...rest }) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => formatBRL(value));
  const ref = useRef(null);

  // keep in sync with external value changes when not being edited
  useEffect(() => {
    if (!focused) setText(formatBRL(value));
  }, [value, focused]);

  const handleChange = (e) => {
    const { text: t, numeric } = sanitize(e.target.value);
    setText(t);
    onChange && onChange(numeric);
  };

  const handleFocus = (e) => {
    setFocused(true);
    setText(toEditText(value));
    // move cursor to end after value swap
    requestAnimationFrame(() => { try { const el = ref.current; if (el) el.setSelectionRange(el.value.length, el.value.length); } catch (_) { /* noop */ } });
    rest.onFocus && rest.onFocus(e);
  };

  const handleBlur = (e) => {
    setFocused(false);
    setText(formatBRL(value));
    rest.onBlur && rest.onBlur(e);
  };

  const { onFocus, onBlur, ...inputRest } = rest;

  return (
    <div className={`relative ${className}`}>
      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-[#9A8A98]">R$</span>
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={text}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className="input-underline tabular w-full pl-10 text-right disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-70"
        data-testid={testid}
        {...inputRest}
      />
    </div>
  );
}
